import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/8e2e08e1-17a4-4a26-83f0-75c69f2c6ea0.png" 
              alt="Bolwar S.R.L. Logo" 
              className="h-10 w-10 object-contain"
            />
            <h1 className="text-2xl font-semibold text-gray-900">Bolwar S.R.L.</h1>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url(https://public.readdy.ai/ai/img_res/0f470f39e9d78af738575c9bb04195cf.jpg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <p className="text-sm uppercase tracking-widest text-gray-400 mb-6">Innovative iOS Solutions</p>
          <h2 className="text-6xl md:text-7xl font-serif text-white leading-tight mb-16">
            Crafting Premium<br />
            iOS Applications<br />
            For Modern Users
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-5 mt-20">
            {[
              { icon: 'ri-shield-check-line', label: 'Security First' },
              { icon: 'ri-speed-line', label: 'High Performance' },
              { icon: 'ri-cloud-line', label: 'Cloud Sync' },
              { icon: 'ri-smartphone-line', label: 'Native iOS' },
              { icon: 'ri-lightbulb-line', label: 'Innovation' }
            ].map((feature, index) => (
              <div key={index} className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 flex flex-col items-center justify-center gap-3 border border-slate-700/50 hover:bg-slate-700/50 transition-all duration-300">
                <i className={`${feature.icon} text-3xl text-white`}></i>
                <span className="text-sm text-white font-medium whitespace-nowrap">{feature.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="min-h-screen flex items-center">
        <div className="w-full grid md:grid-cols-2">
          {/* Left Half - Image */}
          <div className="relative min-h-[600px] md:min-h-screen bg-amber-50">
            <img 
              src="https://readdy.ai/api/search-image?query=modern%20software%20development%20team%20workspace%20with%20multiple%20monitors%20showing%20iOS%20app%20interfaces%20clean%20minimalist%20office%20environment%20with%20natural%20lighting%20professional%20tech%20atmosphere%20warm%20tones%20and%20contemporary%20design&width=960&height=1200&seq=about-img-001&orientation=portrait"
              alt="Development Team"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/10 to-black/30"></div>
            <div className="absolute top-8 left-8">
              <p className="text-xs uppercase tracking-wider text-gray-600 mb-4">Who We Are</p>
            </div>
            <div className="absolute bottom-12 left-12">
              <h3 className="text-7xl font-black text-white leading-none">
                Expert Team<br />
                Building The<br />
                Future
              </h3>
            </div>
          </div>

          {/* Right Half - Content */}
          <div className="bg-white flex items-center justify-center px-12 py-20">
            <div className="max-w-xl text-center">
              <h3 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Bolwar S.R.L.<br />
                Development Excellence
              </h3>
              <div className="space-y-5 text-gray-600 text-lg leading-relaxed">
                <p>
                  We are a team of experts dedicated to developing modern applications for the iOS platform. Our passion lies in creating innovative solutions that enhance user experience and improve the security of mobile devices.
                </p>
                <p>
                  With years of experience in iOS development, we specialize in building applications that combine cutting-edge technology with intuitive design. Our products are crafted with attention to detail and a deep understanding of user needs.
                </p>
                <p>
                  At Bolwar S.R.L., we believe in the power of technology to transform everyday experiences. Our commitment to excellence drives us to continuously innovate and deliver products that exceed expectations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start justify-between mb-16">
            <h3 className="text-6xl font-bold text-gray-900 leading-tight">
              Our Premium<br />
              iOS Applications
            </h3>
            <p className="text-lg text-gray-600 max-w-md mt-4">
              Designed to enhance your mobile experience with powerful features and elegant interfaces.
            </p>
          </div>

          <div className="grid md:grid-cols-12 gap-6">
            {/* VPN Product - Larger Card */}
            <div className="md:col-span-5 bg-amber-50 rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="mb-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20VPN%20mobile%20app%20interface%20on%20iPhone%20screen%20showing%20secure%20connection%20with%20shield%20icon%20and%20world%20map%20clean%20blue%20and%20white%20design%20professional%20security%20app%20mockup%20simple%20background&width=400&height=600&seq=vpn-app-001&orientation=portrait"
                  alt="VPN Application"
                  className="w-full h-96 object-contain"
                />
              </div>
              <h4 className="text-3xl font-bold text-gray-900 mb-4">SecureVPN Pro</h4>
              <p className="text-gray-700 leading-relaxed">
                Advanced encryption protocols ensuring your privacy and security across all networks. Seamless connection with global server access, protecting your data wherever you go. Experience true online freedom with military-grade security.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-700">256-bit Encryption</span>
                <span className="px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-700">No Logs Policy</span>
                <span className="px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-700">Global Servers</span>
              </div>
            </div>

            {/* Cleaner Product */}
            <div className="md:col-span-4 bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="mb-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=iOS%20cleaner%20app%20interface%20on%20iPhone%20displaying%20storage%20optimization%20with%20colorful%20circular%20charts%20and%20clean%20buttons%20modern%20minimalist%20design%20with%20gradient%20colors%20simple%20white%20background&width=400&height=600&seq=cleaner-app-001&orientation=portrait"
                  alt="Cleaner Application"
                  className="w-full h-96 object-contain"
                />
              </div>
              <h4 className="text-3xl font-bold text-gray-900 mb-4">CleanOS Master</h4>
              <p className="text-gray-700 leading-relaxed">
                Optimize your device performance with intelligent cleaning algorithms. Remove junk files, manage storage, and boost speed with just one tap. Keep your iPhone running like new.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="px-4 py-2 bg-gray-100 rounded-full text-sm font-medium text-gray-700">Smart Clean</span>
                <span className="px-4 py-2 bg-gray-100 rounded-full text-sm font-medium text-gray-700">Storage Manager</span>
              </div>
            </div>

            {/* Coming Soon */}
            <div className="md:col-span-3 bg-white rounded-3xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col items-center justify-center text-center">
              <div className="w-full h-64 mb-8 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=abstract%20futuristic%20iOS%20app%20concept%20with%20blurred%20interface%20elements%20and%20glowing%20effects%20suggesting%20innovation%20and%20upcoming%20technology%20soft%20purple%20and%20blue%20tones%20simple%20background&width=400&height=500&seq=coming-soon-001&orientation=portrait"
                  alt="Coming Soon"
                  className="w-full h-full object-contain opacity-60"
                />
              </div>
              <h4 className="text-3xl font-bold text-gray-900 mb-4">Coming Soon</h4>
              <p className="text-gray-600">
                We're working on something exciting. Stay tuned for our next innovative iOS application.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-black rounded-[32px] p-16">
            <div className="grid md:grid-cols-12 gap-12">
              {/* Left Column */}
              <div className="md:col-span-5">
                <h3 className="text-5xl font-serif text-white mb-6 leading-tight">Stay Connected</h3>
                <p className="text-gray-400 text-lg mb-12">Reach out to discuss your iOS development needs</p>
              </div>

              {/* Middle Column */}
              <div className="md:col-span-3">
                <h4 className="text-xs uppercase tracking-widest text-white mb-6 font-semibold">Navigation</h4>
                <ul className="space-y-4">
                  <li><span className="text-white text-base cursor-pointer hover:text-gray-300 transition-colors">Home</span></li>
                  <li><span className="text-white text-base cursor-pointer hover:text-gray-300 transition-colors">About</span></li>
                  <li><span className="text-white text-base cursor-pointer hover:text-gray-300 transition-colors">Products</span></li>
                  <li><span className="text-white text-base cursor-pointer hover:text-gray-300 transition-colors">Contact</span></li>
                </ul>
              </div>

              {/* Right Column */}
              <div className="md:col-span-4">
                <h4 className="text-xs uppercase tracking-widest text-white mb-6 font-semibold">Contact</h4>
                <div className="space-y-4 mb-10">
                  <div className="flex items-center gap-3">
                    <i className="ri-phone-line text-white text-xl w-6 h-6 flex items-center justify-center"></i>
                    <a href="tel:+14347889791" className="text-white text-base hover:text-gray-300 transition-colors">+1 4347889791</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-mail-line text-white text-xl w-6 h-6 flex items-center justify-center"></i>
                    <a href="mailto:info@bolwarsrl.com" className="text-white text-base hover:text-gray-300 transition-colors">info@bolwarsrl.com</a>
                  </div>
                </div>

                <h4 className="text-xs uppercase tracking-widest text-white mb-6 font-semibold">Location</h4>
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-white text-xl w-6 h-6 flex items-center justify-center mt-1"></i>
                  <address className="text-white text-base not-italic leading-relaxed">
                    San Francisco de Heredia,<br />
                    de la Escuela de San Fco<br />
                    Heredia, 40103<br />
                    Costa Rica
                  </address>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="mt-16 pt-8 border-t border-gray-800">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <p className="text-gray-500 text-sm">© 2025 Bolwar S.R.L. All rights reserved</p>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-500 text-sm hover:text-gray-300 transition-colors">
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
